import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

// RA note (2026-10) — one-press requests from the scope row, in this order. Mob is the exception:
// it asks "Update lineout?" — Yes opens the FRS with the scope ready for the Mob request, No sends
// the open rooms straight to the Schedule Pool.
export const REQUEST_KINDS = [
  ["fm", "FM", "Field measure"], ["jc", "JC", "Job check"], ["del", "Del", "Delivery"],
  ["mob", "Mob", "Mobilization"], ["pl", "PL", "Punchlist"],
];

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "2-digit" }) : "");
const PILL = "inline-flex h-5 items-center rounded-full border px-1.5 text-[10px] font-semibold leading-none transition-colors";
const OFF = "border-gray-300 bg-white text-gray-600 hover:border-gray-500 hover:text-gray-900";
const ON = "border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600";
const REQ = "border-amber-400 bg-amber-300 text-amber-950 hover:bg-amber-400 dark:border-amber-500 dark:bg-amber-500 dark:text-amber-950";
const TONE = { yellow: REQ, green: ON };
const BOARD = { schedule: ["/schedule", "Schedule board"], dispatch: ["/dispatch", "Dispatch board"] };
const range = (a, b) => (a ? (b && b !== a ? `${fmt(`${a}T00:00:00`)}–${fmt(`${b}T00:00:00`)}` : fmt(`${a}T00:00:00`)) : "not dated yet");
const STATE_TEXT = { yellow: "Requested — on the Schedule board, not dispatched yet", green: "Scheduled — on the Dispatch board",
                     open: "Requested — open until the PM or foreman closes it" };

/** P1-3 — the state of a live request, its dates and a way to its board. */
function StateBody({ st, label, sc, testId }) {
  const navigate = useNavigate();
  const [path, board] = BOARD[st.board] || [];
  return (
    <>
      <div className="text-xs font-semibold text-foreground">{label} — {sc.scope}</div>
      <div className={`mt-1 inline-flex rounded-full px-1.5 text-[10px] font-semibold ${TONE[st.state]}`} data-testid={`${testId}-state`}>
        {STATE_TEXT[st.board ? st.state : "open"]}
      </div>
      {st.cards ? (
        <ul className="mt-2 space-y-1 text-[11px]" data-testid={`${testId}-cards`}>
          {st.cards.map((c) => (
            <li key={c.id} className="flex justify-between gap-2">
              <span className="truncate font-medium text-foreground">{c.title || "Mob"}</span>
              <span className="whitespace-nowrap text-muted-foreground">{range(c.start, c.end)} · {c.crew || "?"} crew · {c.status}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-1 text-[11px] text-muted-foreground">Requested by {st.by || "someone"} on {fmt(st.at)}</div>
      )}
      {st.note && <div className="mt-1 whitespace-pre-wrap text-xs text-foreground" data-testid={`${testId}-note-text`}>{st.note}</div>}
      {path && (
        <button type="button" onClick={() => navigate(path)} data-testid={`${testId}-board`}
                className="mt-2 block text-[11px] font-medium text-sky-700 underline hover:text-sky-900 dark:text-sky-400">Open on {board}</button>
      )}
    </>
  );
}

function RequestPill({ project, sc, kind, short, label, onChanged, onMob }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const live = sc.requests?.[kind];
  const node = sc.node || null;                       // RA tree — a part of the scope ("" = whole scope)
  const testId = `ra-request-${kind}-${project.id}-${sc.scope}${sc.node_key ? `-${sc.node_key}` : ""}`;
  const above = !live && sc.inherited?.[kind];        // requested higher up the tree
  const close = async () => {
    setBusy(true);
    try {
      await api.post(`/scope-requests/${live.id}/close`);
      toast.success(`${label} closed — ${sc.scope}`);
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not close the request"); }
    finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/scope-requests`, { scope: sc.scope, kind, note, ...(node || {}) });
      toast.success(`${label} requested — ${project.project_name} · ${sc.scope}${sc.where ? ` · ${sc.where}` : ""}`, {
        description: data?.pt ? (data.pt.ticket ? `Pull ticket ${data.pt.ticket.ticket_number} sent to the warehouse` : data.pt.note) : undefined });
      setOpen(false); setNote("");
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not send the request"); }
    finally { setBusy(false); }
  };
  const st = sc.request_state?.[kind];
  if (kind === "mob" && st) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testId} data-open="1" data-state={st.state}
                  title={`${STATE_TEXT[st.state]} · ${range(st.start, st.end)}`} className={`${PILL} ${TONE[st.state]}`}>{short}</button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-3" data-testid={`${testId}-pop`}>
          <StateBody st={st} label={label} sc={sc} testId={testId} />
          <div className="mt-2 flex justify-end">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setOpen(false); onMob(sc); }}
                    data-testid={`${testId}-more`}>Request more rooms</Button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
  if (live) {
    const ls = st || { state: "yellow", by: live.by, at: live.at, note: live.note, board: "" };
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testId} data-open="1" data-state={ls.state}
                  title={`${label} requested by ${live.by || "someone"} on ${fmt(live.at)}${live.note ? ` — ${live.note}` : ""}`}
                  className={`${PILL} ${TONE[ls.state]}`}>{short}</button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-3" data-testid={`${testId}-pop`}>
          <StateBody st={ls} label={label} sc={sc} testId={testId} />
          <div className="mt-2 flex justify-end gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpen(false)}>Keep open</Button>
            <Button size="sm" className="h-7 text-xs" disabled={busy} onClick={() => { setOpen(false); close(); }}
                    data-testid={`${testId}-close`}>Close request</Button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
  if (above) {
    return (
      <span data-testid={testId} data-open="0" data-above="1" title={`${label} already requested at ${above.where || "the scope"} by ${above.by || "someone"} on ${fmt(above.at)}`}
            className={`${PILL} border-dashed border-amber-400 bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300`}>{short}</span>
    );
  }
  if (kind === "mob") {
    return (
      <button type="button" onClick={() => onMob(sc)} data-testid={testId} data-open="0"
              title="Request a mobilization for this scope" className={`${PILL} ${OFF}`}>{short}</button>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} data-open="0" title={`Request a ${label.toLowerCase()} for ${sc.scope}`}
                className={`${PILL} ${OFF}`}>{short}</button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3" data-testid={`${testId}-pop`}>
        <div className="text-xs font-semibold text-foreground">{label} — {sc.scope}{sc.where ? ` · ${sc.where}` : ""}</div>
        <div className="mt-0.5 text-[11px] text-muted-foreground">The PM and foreman are told in the app.</div>
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)"
                  className="mt-2 text-xs" data-testid={`${testId}-note`} />
        <div className="mt-2 flex justify-end gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpen(false)}>Cancel</Button>
          <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={send}
                  data-testid={`${testId}-send`}>Request</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// P3-1 — the scope's picking tickets: yellow Requested / Not On Hand · green Pulled / Ready · blue Delivered.
const PT_TONE = { yellow: REQ, green: ON, blue: "border-sky-500 bg-sky-500 text-white hover:bg-sky-600" };
const PT_TEXT = { yellow: "Requested — waiting on the warehouse", green: "Pulled / ready for delivery", blue: "Delivered" };
function PtPill({ pt, testId }) {
  const navigate = useNavigate();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} data-state={pt.state} title={`Picking tickets · ${PT_TEXT[pt.state]}`}
                className={`${PILL} ${PT_TONE[pt.state]}`}>PT</button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-3" data-testid={`${testId}-pop`}>
        <div className="text-xs font-semibold text-foreground">Pull tickets</div>
        <ul className="mt-2 space-y-1.5">
          {pt.tickets.map((t) => (
            <li key={t.id} className="flex items-center justify-between gap-2 text-[11px]" data-testid={`${testId}-ticket-${t.number}`}>
              <span className="font-medium text-foreground">{t.number}{t.auto ? <span className="ml-1 text-muted-foreground">auto</span> : null}</span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                {t.need_by ? `need ${fmt(`${t.need_by}T00:00:00`)}` : "no need-by"}
                <span className={`rounded-full px-1.5 text-[10px] font-semibold ${PT_TONE[t.state]}`}>{t.status}</span>
              </span>
            </li>
          ))}
        </ul>
        <button type="button" onClick={() => navigate("/warehouse")} data-testid={`${testId}-open`}
                className="mt-2 text-[11px] font-medium text-sky-700 underline hover:text-sky-900 dark:text-sky-400">Open the Warehouse</button>
      </PopoverContent>
    </Popover>
  );
}

export function RequestPills({ project, sc, onChanged, onMob }) {
  const pt = sc.request_state?.pt;
  return (
    <div className="flex items-center gap-1" data-testid={`ra-requests-${project.id}-${sc.scope}${sc.node_key ? `-${sc.node_key}` : ""}`}>
      {REQUEST_KINDS.map(([kind, short, label]) => (
        <RequestPill key={kind} project={project} sc={sc} kind={kind} short={short} label={label} onChanged={onChanged} onMob={onMob} />
      ))}
      {pt && <PtPill pt={pt} testId={`ra-request-pt-${project.id}-${sc.scope}`} />}
    </div>
  );
}

/** "Update lineout?" — Yes → the job's FRS with the scope pre-selected for the Mob request;
 *  No → every open room of the scope goes to the Schedule Pool as one card, right now. */
export function MobRequestDialog({ target, onClose, onChanged }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const { project, sc } = target;
  const direct = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/scope-requests`, { scope: sc.scope, kind: "mob", ...(sc.node || {}) });
      toast.success(`Mob requested — ${data.rooms} ${sc.scope} room${data.rooms === 1 ? "" : "s"}${sc.where ? ` (${sc.where})` : ""} sent to the Schedule Pool`,
                    { description: "A pull ticket for the material goes to the warehouse automatically." });
      onClose(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not request the mobilization"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="ra-mob-dialog">
        <DialogHeader>
          <DialogTitle>Update lineout?</DialogTitle>
          <DialogDescription>
            {project.project_name} · {sc.scope}{sc.where ? ` · ${sc.where}` : ""}. <b>Yes</b> opens the FRS so you can pick the rooms and dates for this Mob.
            <b> No</b> sends every open {sc.scope} room{sc.where ? ` in ${sc.where}` : ""} to the Schedule Pool as one card now.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={direct} data-testid="ra-mob-no">No — send to the pool</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ra-mob-yes"
                  onClick={() => { onClose(); navigate(`/projects/${project.id}?tab=frs&mobScope=${encodeURIComponent(sc.scope_type || sc.scope)}`); }}>
            Yes — open the FRS
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
